import { List as ImmutableList } from 'immutable';
import type { SagaIterator } from 'redux-saga';
import { call, put, race, select, take, takeEvery, takeLeading } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { resourceSagaUtils, resourceStoreTypes } from 'app-components/flux/core';
import { getLogger, httpClientInterface, tracker } from '../../utils/core';
import { featureFlagsSelectors } from '../feature-flags';
import { filtersActions } from '../filters';
import { goalsActions } from '../goals';
import { transactionsActions } from '../transactions';
import * as actions from './freeToSpendActions';
import { getFreeToSpendById, getLastSyncDate } from './freeToSpendSelectors';
import { transformRequestDataToFreeToSpend, transformResponseToFreeToSpend } from './freeToSpendTransformers';
import { mkFreeToSpend } from './freeToSpendTypes';
import type { FreeToSpend, FreeToSpendProps, PlannedSpendingItem } from './freeToSpendTypes';

const log = getLogger('flux/free-to-spend');

const mkQcsSyncF2SConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<FreeToSpendProps>();

const mkResourceConfig = (props: any) =>
  mkQcsSyncF2SConfig({
    resourceName: 'free to spend',
    resourceBaseUrl: '/free-to-spend',
    getLastSyncDate,
    transformResourceToRequestData: transformRequestDataToFreeToSpend,
    transformResponseToResources: transformResponseToFreeToSpend,
    ...props,
  });

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('freeToSpendDumpUpdater');
export function* freeToSpendDumpUpdater(resources: Array<FreeToSpend>) {
  yield callFirst(freeToSpendDumpUpdaterFull, resources);
  yield callLast(freeToSpendNudgeUpdater);
}

function* freeToSpendDumpUpdaterFull(resources: Array<FreeToSpend>): SagaIterator {
  const bumpSpendingPlan: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpSpendingPlan');
  if (bumpSpendingPlan && resources) {
    const freeToSpends = resources.map((resource: FreeToSpend) => mkFreeToSpend(resource));
    yield put(actions.updateFreeToSpendsBump({ resources: freeToSpends }));
  }
}

export function* freeToSpendNudgeUpdater(): SagaIterator {
  const process: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpSpendingPlan');
  if (process) {
    yield put(actions.getFreeToSpend());
  }
}

export function* getFreeToSpendActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.getFreeToSpendSuccess,
    failureAction: actions.getFreeToSpendFailure,
  });
  yield resourceSagaUtils.takeAndDebounce(
    actions.getFreeToSpend,
    resourceSagaUtils.qcsSyncGetResources,
    resourceConfig,
  );
}

export function* createFreeToSpendActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.createFreeToSpendSuccess,
    failureAction: actions.createFreeToSpendFailure,
  });
  yield takeEvery(
    actions.createFreeToSpend.type,
    resourceSagaUtils.qcsSyncCreateResource<FreeToSpendProps>,
    resourceConfig,
  );
}

function* fetchFreeToSpend(action: any) {
  yield put(actions.getFreeToSpend(null as any, { ...action.meta }));
}

function* triggerDeletePlannedItem(action: any) {
  try {
    // this delete request is using "POST" so it can't be done with qcsSyncDeleteResource
    const requestUrl = `${httpClientInterface.getServiceUrl()}/free-to-spend/planned-spending-item/delete/${
      action.payload.id
    }`;
    yield call(httpClientInterface.send, 'POST', requestUrl);
    if (action?.meta?.resolve && typeof action.meta.resolve == 'function') {
      yield call(action.meta.resolve);
    }
  } catch (error) {
    log.error('delete planned item failed with error ', error);
    if (action?.meta?.reject && typeof action.meta.reject == 'function') {
      yield call(action.meta.reject, error);
    }
  }
  yield put(actions.getFreeToSpend(null as any, {}));
}

export function* deletePlannedSpendingActionWatcher() {
  yield takeEvery(actions.deletePlannedSpending, triggerDeletePlannedItem);
}

export function* doBatchReleaseActionWatcher() {
  const resourceConfig = resourceStoreTypes.mkQcsSyncResourceConfig({
    resourceBaseUrl: (action) => `/free-to-spend/planned-spending/release-unused-funds?date=${action.payload}Z`,
    successAction: actions.getFreeToSpend,
    failureAction: undefined,
  });
  yield takeEvery(actions.doBatchRelease.type, resourceSagaUtils.qcsSyncCreateResource<any>, resourceConfig);
}

export function* updateFreeToSpendActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.updateFreeToSpendSuccess,
    failureAction: actions.updateFreeToSpendFailure,
  });
  yield takeLeading(
    actions.updateFreeToSpend.type,
    resourceSagaUtils.qcsSyncUpdateResource<FreeToSpendProps>,
    resourceConfig,
  );
}

export function* updatePlannedItemActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.updatePlannedSpendingSuccess,
    failureAction: actions.updatePlannedSpendingFailure,
  });
  yield takeEvery(
    actions.updatePlannedSpending.type,
    resourceSagaUtils.qcsSyncUpdateResource<FreeToSpendProps>,
    resourceConfig,
  );
}

export function* deleteFreeToSpendActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.deleteFreeToSpendSuccess,
    failureAction: actions.deleteFreeToSpendFailure,
  });
  yield takeEvery(
    actions.deleteFreeToSpend.type,
    resourceSagaUtils.qcsSyncDeleteResource<FreeToSpendProps>,
    resourceConfig,
  );
}

export function* upsertFreeToSpendFailureActionWatcher() {
  yield takeEvery(
    [actions.createFreeToSpendFailure, actions.updateFreeToSpendFailure, actions.deleteFreeToSpendFailure],
    fetchFreeToSpend,
  );
}

export function* freeToSpendModifiedActionWatcher() {
  yield takeEvery(
    [actions.createFreeToSpendSuccess, actions.updateFreeToSpendSuccess, actions.deleteFreeToSpendSuccess],
    fetchFreeToSpend,
  );
}

export function* plannedSpendingActionWatcher() {
  yield takeEvery(actions.createPlannedSpending, freeToSpendCreatePlannedSpending);
}

// TODO: consolidate this with mobile and create implementation
// need to make and return a filter and its corresponding filter id
function* freeToSpendCreatePlannedSpending(action: ReturnType<typeof actions.createPlannedSpending>) {
  // Send the filterItems to the createFilter Saga and capture the returned filterID
  yield put(filtersActions.createFilter(action.payload.filterItems));
  const freeToSpend: FreeToSpend = yield select(getFreeToSpendById, action.payload?.id);
  const { filterSuccess } = yield race({
    filterSuccess: take(filtersActions.createFilterSuccess),
    filterFailure: take(filtersActions.createFilterFailure),
  });
  if (filterSuccess && freeToSpend) {
    const filterId = filterSuccess.payload?.id;
    const { targetAmount, name, recurring, rolloverType, resetRolloverAmount } = action.payload;
    const existingPlannedSpending = freeToSpend.plannedSpendingItems || ImmutableList<PlannedSpendingItem>();
    const payload = freeToSpend.merge({
      plannedSpendingItems: existingPlannedSpending.push({
        id: '',
        clientId: uuid(),
        name,
        targetAmount,
        filterId,
        recurring,
        rolloverType,
        resetRolloverAmount,
      }),
    });
    yield put(actions.updateFreeToSpend(payload));
  }
}
function* transactionModified(action: any) {
  const context = action?.meta?.context;
  if (
    context &&
    (context === 'Reg_Income' ||
      context === 'Reg_BillsAndSubscriptions' ||
      context === 'Reg_Savings' ||
      context === 'Reg_Spendings' ||
      context === 'Reg_FTS' ||
      context === 'Reg_FTS_2' ||
      context === 'addAccounts' ||
      context === 'spending-plan' ||
      context === 'spending-plan-goals' ||
      context === 'scheduledTransactions') &&
    (action.type !== transactionsActions.getTransactionsSuccess.toString() || action?.payload?.resources?.length > 0)
  ) {
    tracker.track(tracker.events.txnUpdatedInFTSRegister, { description: context });
    yield put(actions.getFreeToSpend({} as any, { context }));
  }
}

function* transactionModifiedActionWatcher() {
  yield takeEvery(transactionsActions.getTransactionsSuccess, transactionModified);
  yield takeEvery(transactionsActions.updateTransactionSuccess, transactionModified);
  yield takeEvery(transactionsActions.performTransactionActionSuccess, transactionModified);
  yield takeEvery(transactionsActions.batchTransactionsSuccess, transactionModified);
  yield takeEvery(transactionsActions.deleteTransactionSuccess, transactionModified);
  yield takeEvery(transactionsActions.createTransactionSuccess, transactionModified);
}

/* Eventually QCS will detect goal changes automatically */
function* goalsModified(action: any) {
  if (action?.meta?.context === 'spending-plan' || action?.meta?.context === 'spending-plan-goals') {
    tracker.track(tracker.events.goalUpdatedInFTSRegister);
    yield put(actions.getFreeToSpend());
  }
}

function* goalsWatcher() {
  yield takeEvery(goalsActions.updateGoalSuccess, goalsModified);
  yield takeEvery(goalsActions.createGoalSuccess, goalsModified);
  yield takeEvery(goalsActions.deleteGoalSuccess, goalsModified);
}

export default [
  plannedSpendingActionWatcher,
  getFreeToSpendActionWatcher,
  createFreeToSpendActionWatcher,
  updateFreeToSpendActionWatcher,
  deleteFreeToSpendActionWatcher,
  upsertFreeToSpendFailureActionWatcher,
  freeToSpendModifiedActionWatcher,
  transactionModifiedActionWatcher,
  updatePlannedItemActionWatcher,
  goalsWatcher,
  deletePlannedSpendingActionWatcher,
  doBatchReleaseActionWatcher,
];
